import { useCallback, useEffect, useRef } from 'react';
import useMemoizedFn from './useMemoizedFn';

const isNumber = (value: unknown): value is number => typeof value === 'number';
const useInterval = (fn: () => void, delay?: number, options: { immediate?: boolean } = {}) => {
  const timerCallback = useMemoizedFn(fn);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    if (!isNumber(delay) || delay < 0) {
      return;
    }
    if (options.immediate) {
      timerCallback();
    }
    timerRef.current = setInterval(timerCallback, delay);
  }, [delay, options.immediate, timerCallback, clear]);

  useEffect(() => {
    return clear;
  }, [clear]);

  return { start, clear };
};

export default useInterval;
